import {
  TouchableOpacity,
  View,
  ScrollView,
  Image,
  StyleSheet,
  Dimensions,
} from 'react-native';
import {LotteryModeItem, getLottoList} from './mix-lottery-service';
import React from 'react';
import LazyImage from '@/components/basic/image';
import Text from '@/components/basic/text';
import theme from '@/style';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';
import {getHMSbyTimer} from '@/utils';
import {BasicObject} from '@/types';
import CheckIcon from '@/components/svgs/basic/checked';

export interface LotteryPickerProps {
  lotteryID?: number;
  onClose?: () => void;
  isOpen?: boolean;
  onItemSelect?: (id: number, isQuick?: string, lotteryName?: string) => void;
  isQuick?: boolean;
}

const LotteryPicker = ({
  isOpen,
  onClose,
  lotteryID,
  onItemSelect,
}: LotteryPickerProps) => {
  const [list, setList] = React.useState<LotteryModeItem[]>([]);
  const [quickList, setQuickList] = React.useState<LotteryModeItem[]>([]);
  const timer = React.useRef(new Date().getTime());
  const updateList = async () => {
    try {
      const res = await getLottoList();
      setList(res);
    } finally {
    }
  };

  const updateQuick = async () => {
    try {
      const res = await getLottoList(true);
      setQuickList(res);
    } finally {
    }
  };

  React.useEffect(() => {
    updateList();
    updateQuick();
  }, []);
  React.useEffect(() => {
    if (isOpen) {
      updateList();
      updateQuick();
    }
  }, [isOpen]);

  const targetList = React.useMemo(() => {
    const normalOpen = list
      .filter(item => item.lessSec > 0)
      .sort((a, b) => a.lessSec - b.lessSec);
    const normalClose = list.filter(item => item.lessSec <= 0);
    return [...quickList, ...normalOpen, ...normalClose];
  }, [list, quickList]);

  return (
    <View style={[styles.sheet]}>
      <View style={[theme.flex.row, theme.flex.between, styles.header]}>
        <Text style={styles.headerTitle}>Please Choose</Text>
        <NativeTouchableOpacity
          onPress={() => onClose?.()}
          style={styles.closeButton}>
          <Image
            style={[theme.icon.m]}
            source={require('@components/assets/icons/close.webp')}
          />
        </NativeTouchableOpacity>
      </View>
      <ScrollView
        style={[styles.scroll]}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[styles.scrollContent]}>
        {targetList.map((item, _i) => (
          <Item
            onPress={() =>
              onItemSelect?.(
                item.lotteryID,
                item.isQuick ? '1' : '0',
                item.lotteryName,
              )
            }
            info={item}
            key={_i}
            isQuick={item.isQuick}
            timer={timer.current}
            isSelected={lotteryID === item.lotteryID}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const Item = ({
  info,
  timer,
  isSelected,
  onPress,
  isQuick,
}: {
  info: LotteryModeItem;
  timer: number;
  isSelected?: boolean;
  isQuick?: boolean;
  onPress?: () => void;
}) => {
  const isClosed = React.useMemo(() => {
    return isQuick ? false : info.lessSec <= 0;
  }, [info.lessSec, isQuick]);

  const times = React.useMemo<BasicObject>(() => {
    if (info.lessSec > 0) {
      const duration = Math.max(
        0,
        info.lessSec - Math.round((new Date().getTime() - timer) / 1000),
      );
      const {seconds, minutes, hours, days} = getHMSbyTimer(
        duration * 1000,
        true,
      );
      return {
        seconds,
        minutes,
        hours,
        days,
      };
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.lessSec]);

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isClosed}
      activeOpacity={0.8}
      style={[
        theme.flex.row,
        theme.flex.between,
        theme.flex.centerByCol,
        styles.itemRow,
        isClosed && styles.closedRow,
        isSelected && styles.selectedRow,
      ]}>
      <View style={[theme.flex.row, theme.flex.centerByCol]}>
        <LazyImage
          imageUrl={info.icon}
          width={28}
          radius={14}
          height={28}
          occupancy="transparent"
        />
        <Text
          style={[theme.margin.lefts, styles.itemName]}
          color={theme.basicColor.dark}>
          {info.lotteryName}
        </Text>
      </View>
      <View style={[styles.rightWrap, theme.flex.row, theme.flex.centerByCol]}>
        {isClosed && (
          <Text
            fontFamily="fontInterBold"
            size="medium"
            style={[styles.timeText]}>
            Closed
          </Text>
        )}
        {!isClosed && !isQuick && (
          <View style={[theme.flex.alignEnd, styles.timeWrap]}>
            {times.days > 0 && (
              <Text
                fontFamily="fontInterBold"
                size="medium"
                style={[styles.timeText]}>
                {times.days} Day
              </Text>
            )}
            <Text
              fontFamily="fontInterBold"
              size="medium"
              style={[styles.timeText]}>
              {times.hours}:{times.minutes}:{times.seconds}
            </Text>
          </View>
        )}
        {isSelected && <CheckIcon />}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  sheet: {
    maxHeight: Dimensions.get('window').height * 0.5,
  },
  header: {
    minHeight: 56,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F2F7',
  },
  headerTitle: {
    color: '#31373D',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  },
  closeButton: {
    padding: 2,
    marginRight: -2,
  },
  scroll: {
    maxHeight: Dimensions.get('window').height * 0.5 - 56,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  itemRow: {
    minHeight: 48,
    paddingHorizontal: 12,
  },
  selectedRow: {
    backgroundColor: '#F4F7FF',
  },
  closedRow: {
    opacity: 0.3,
  },
  rightWrap: {
    minWidth: 96,
    justifyContent: 'flex-end',
    gap: 8,
  },
  timeWrap: {
    minWidth: 88,
  },
  itemName: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
  },
  timeText: {
    color: '#31373D',
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '700',
    textAlign: 'right',
  },
});

export default LotteryPicker;
